<template>
  <div :id="`graph--position${graphID}`">
    <div class="echarts">
      <IEcharts
        :id="`graphID${graphID}`"
        :key="reloadGraph"
        :loading="loading"
        :option="bar"
        :theme="theme"
        :resizable="resizable"
        @ready="onChartReady"
        @click="onClickHandler"
      />
    </div>
    <div :id="`overlay${graphID}`" @click="toggleOverlay" />
  </div>
</template>

<script>
import { EventBus } from "@/services/event-bus";
import { colorMixin } from "@/util/mixin/index.js";
import IEcharts from "vue-echarts-v3/src/full.js";

export default {
  components: { IEcharts },
  mixins: [colorMixin],
  props: {
    graphID: {
      type: [Number, String],
      default: 1,
    },
    series: {
      type: Array,
      required: true,
      default: function () {
        return [];
      },
    },
    visualMap: {
      type: Object,
      default: function () {
        return {};
      },
    },
    xAxis: {
      type: [Array, Object],
    },
    xAxisData: {
      type: Array,
      default: function () {
        return [];
      },
    },
    yAxis: {
      type: Array,
      default: function () {
        return [
          {
            type: "value",
          },
        ];
      },
    },
    title: {
      type: Object,
      default: function () {
        return {};
      },
    },
    grid: {
      type: Array,
      default: function () {
        return [];
      },
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    legendPosition: {
      type: Object,
      default: function () {
        return {};
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      required: true,
    },
    isOnClickValueRequired: {
      type: Boolean,
      default: false,
    },
    isHoverFix: {
      type: Boolean,
      default: true,
    },
    toolBoxPosition: {
      type: Object,
      default: function () {
        return {
          right: 100,
        };
      },
    },
    isMagicType: {
      type: Boolean,
      default: true,
    },
    isDownload: {
      type: Boolean,
      default: true,
    },
    popUpWidth: {
      type: String,
      default: "10vw",
    },
  },
  data() {
    return {
      resizable: true,
      selectedToolboxOption: null,
      legendPerCol: 14,
      bar: {
        backgroundColor: "",
        legend: {
          show: true,
          type: "scroll",
          width: "75%",
          bottom: 50,
          textStyle: {
            color: "",
          },
        },
        grid: [{ left: "15%", right: "10%", bottom: 100 }],
        tooltip: {
          trigger: "axis",
          textStyle: {
            color: "#FBF2ED",
          },
          position: function (pos, params, dom, rect, size) {
            let obj = { top: 5 };
            obj[["left", "right"][+(pos[0] < size.viewSize[0] / 2)]] = 5;
            return obj;
          },
          formatter: (params) => this.formatter(params),
        },
        title: {
          textStyle: {},
        },
        xAxis: {
          data: [],
        },
        toolbox: {
          showTitle: false,
          feature: {
            magicType: {
              show: this.isMagicType,
              type: ["line", "bar"],
            },
            restore: {},
            saveAsImage: {
              show: this.isDownload,
            },
          },
        },
        axisPointer: {
          link: [{ xAxisIndex: "all" }],
        },
        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
          {
            start: 0,
            end: 100,
            type: "slider",
            handleIcon:
              "M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
            handleSize: "70%",
            backgroundColor: "",
            dataBackground: {
              lineStyle: {
                color: "#FBF2ED",
                width: 1,
                opacity: 0.2,
              },
              areaStyle: {
                color: "#1c2747",
              },
            },
          },
        ],
        yAxis: [{}],
        series: [],
      },
      dataLength: null,
      reloadGraph: 0,
    };
  },
  computed: {
    textColor() {
      return this.theme === "dark" ? "#FBF2ED" : "#333333";
    },
  },
  mounted() {
    this.setupOverlay();
    this.initializeChart();
  },
  beforeDestroy() {
    this.bar.series = [];
    this.bar.xAxis.data = [];
  },
  methods: {
    setupOverlay() {
      document.querySelector(`#graph--position${this.graphID}`).style.position =
        "relative";
      Object.assign(document.querySelector(`#overlay${this.graphID}`).style, {
        position: "fixed",
        display: "none",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 2,
        cursor: "pointer",
      });
    },
    initializeChart() {
      this.updateGraphData();

      const positionKeyList = ["top", "bottom", "left", "right"];
      positionKeyList.forEach((el) => {
        if (this.toolBoxPosition[el]) {
          this.bar.toolbox[el] = this.toolBoxPosition[el];
        }
      });

      if (!!this.dataLength && this.series[0].type === "heatmap") {
        this.$set(this.bar, "visualMap", {});
        this.bar["visualMap"] = this.visualMap;
        delete this.bar.dataZoom;
        this.bar.tooltip = {
          type: "item",
          position: "top",
          backgroundColor: "#293257",
          textStyle: {
            color: "#FBF2ED",
          },
        };
      }

      if (this.grid.length) {
        this.bar.grid = [...this.grid];
      }
      this.bar.yAxis = this.yAxis && this.yAxis.length > 0 ? this.yAxis : [{ type: "value" }];

      if (!!this.dataLength && this.series[0].type !== "pie") {
        this.$set(this.bar, "xAxis", { type: "category" });
        if (this.xAxisData.length) {
          this.$set(this.bar.xAxis, "data", []);
          this.bar.xAxis.data = [...this.xAxisData];
        } else {
          if (this.xAxis) {
            this.bar.xAxis = this.xAxis;
          }
        }
      }
      this.bar.title = this.title;
      this.bar.legend["show"] = this.showLegend;
      const legendPosKeys = Object.keys(this.legendPosition);
      if (legendPosKeys.length) {
        legendPosKeys.forEach((key) => {
          this.bar.legend[key] = this.legendPosition[key];
        });
      }

      if (!!this.dataLength && this.series[0].type === "pie") {
        this.bar.tooltip = {
          type: "item",
          position: "top",
          backgroundColor: "#293257",
          textStyle: {
            color: "#FBF2ED",
          },
        };
        this.bar.legend["right"] = "33%";
        this.bar.legend.bottom = 0;
        delete this.bar.tooltip.position;
        delete this.bar.dataZoom;
        delete this.bar.xAxis;
        delete this.bar.yAxis;
      }

      this.graphColorHandler();
    },
    graphColorHandler() {
      const isDark = this.theme === "dark";
      const textColor = isDark ? "#FBF2ED" : "#333333";
      const splitLineColor = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";

      this.$set(this.bar, "backgroundColor", "transparent");
      this.$set(this.bar.title, "textStyle", { color: textColor });
      this.$set(this.bar.legend.textStyle, "color", textColor);

      if (this.bar.tooltip) {
        this.$set(this.bar.tooltip, "backgroundColor", isDark ? "#293257" : "#FFFFFF");
        this.$set(this.bar.tooltip, "borderColor", isDark ? "rgba(83, 122, 228, 0.4)" : "#E0E0E0");
        this.$set(this.bar.tooltip, "borderWidth", 1);
        if (!this.bar.tooltip.textStyle) {
          this.$set(this.bar.tooltip, "textStyle", {});
        }
        this.$set(this.bar.tooltip.textStyle, "color", textColor);
      }

      const excludedType = ["pie", "heatmap"];
      if (!!this.dataLength && !excludedType.includes(this.series[0].type)) {
        for (let y = 0; y < this.bar.yAxis.length; y++) {
          this.$set(this.bar.yAxis[y], "nameTextStyle", { color: textColor });
          this.$set(this.bar.yAxis[y], "axisLine", {
            show: true,
            lineStyle: { color: textColor }
          });
          this.$set(this.bar.yAxis[y], "axisLabel", { color: textColor });
          this.$set(this.bar.yAxis[y], "splitLine", {
            show: true,
            lineStyle: { color: splitLineColor }
          });
          this.$set(this.bar.yAxis[y], "axisTick", {
            lineStyle: { color: textColor }
          });
        }

        if (!this.bar.xAxis.axisLine) {
          this.$set(this.bar.xAxis, "axisLine", { show: true, lineStyle: {} });
        }
        this.$set(this.bar.xAxis.axisLine.lineStyle, "color", textColor);
        
        if (!this.bar.xAxis.axisLabel) {
          this.$set(this.bar.xAxis, "axisLabel", {});
        }
        this.$set(this.bar.xAxis.axisLabel, "color", textColor);

        if (this.bar.dataZoom && this.bar.dataZoom[1]) {
          this.$set(this.bar.dataZoom[1], "backgroundColor", isDark ? "#293258" : "#F5F5F5");
          this.$set(this.bar.dataZoom[1], "textStyle", { color: textColor });
          this.$set(this.bar.dataZoom[1].dataBackground.lineStyle, "color", textColor);
        }
      }
    },
    toggleOverlay() {
      const overlay = document.getElementById(`overlay${this.graphID}`);
      overlay.nextSibling.style.display = "none";
      overlay.style.display = "none";
    },
    fixTableHandler(event) {
      const timeNode = document.createTextNode(this.timeWhileHover);
      const overlay = document.getElementById(`overlay${this.graphID}`);
      const hoverTable = document.getElementById(`hoverTable${this.graphID}`);
      const parentNode = document.createElement("div");
      parentNode.appendChild(timeNode);

      const maxWidth = hoverTable.firstElementChild.offsetWidth - 32;
      hoverTable.style.maxWidth = `${maxWidth}px`;
      hoverTable.style.minWidth = hoverTable.style.width;

      const isDark = this.theme === "dark";
      const styles = {
        display: "block",
        borderStyle: "solid",
        position: "absolute",
        "background-color": isDark ? "#293257" : "#FFFFFF",
        "border-width": "1px",
        "border-color": isDark ? "rgba(83, 122, 228, 0.4)" : "#E0E0E0",
        "border-radius": "4px",
        color: isDark ? "white" : "black",
        padding: "5px",
        top: "0px",
        zIndex: 2,
        whiteSpace: "nowrap",
        boxShadow: "0 2px 10px rgba(0,0,0,0.2)"
      };

      const offsetLeft = document
        .getElementById(`graphID${this.graphID}`)
        .getBoundingClientRect().left;
      const actualGraphWidth = document.getElementById(
        `graphID${this.graphID}`
      ).clientWidth;

      if (event.clientX > actualGraphWidth / 2 + offsetLeft) {
        styles["left"] = "10px";
      } else {
        styles["right"] = "10px";
      }

      Object.assign(parentNode.style, styles);
      parentNode.appendChild(hoverTable);
      overlay.style.display = "block";
      overlay.insertAdjacentElement("afterend", parentNode);
    },
    onClickHandler(event) {
      if (this.isOnClickValueRequired) {
        console.log("graph-click event emitted");
        EventBus.$emit("graph-click", {
          xAxisData: this.bar.xAxis.data,
          eventIndex: event.dataIndex,
        });
      }
    },
    formatter(params) {
      const isDark = this.theme === "dark";
      const textColor = isDark ? "white" : "black";
      let res = `<div style="font-weight: bold">
        <div style="color: ${textColor}">
          ${params[0].name}
        </div>`;
      res += `
          <div style="font-size: 12px; color: #FFC107">
            ${this.$t("Click to pin")} &#x1F4CC;
          </div>
        </div>
        <div id="hoverTable${
          this.graphID
        }" style="resize:horizontal; pointer-events: all"><table>`;
      this.timeWhileHover = params[0].name;
      let column =
        params.length / this.legendPerCol
          ? params.length / this.legendPerCol
          : 1;

      let maxWidth = 170;
      for (let i = 0; i < params.length; i++) {
        let k;
        res += "<tr>";
        for (let j = 0; j < column; j++) {
          if (params[i + j] !== undefined) {
            k = i + j;
            let data = `${params[k].seriesName}: ${
              params[k].value != null ? params[k].value : "N/A"
            }`;
            if (maxWidth < data.length * 7.5) {
              maxWidth = data.length * 7.5;
            }
            res +=
              `<td style="text-align: left; width: ${maxWidth}vw; color: ${textColor}"><div style="display: inline-block; background-color: ${
                params[k].color
              }; width: 10px; height: 10px; border-radius: 50%; margin: 0px 10px;"></div>` +
              data +
              `</td>`;
          }
        }
        res += "</tr>";
        i = k;
      }
      res += "</table></div>";

      this.$nextTick(() => {
        const hoverTableEl = document.querySelector(`#hoverTable${this.graphID}`);
        if (hoverTableEl && hoverTableEl.parentNode) {
          hoverTableEl.parentNode.style.backgroundColor = isDark ? "#293257" : "#FFFFFF";
        }
        if (hoverTableEl) {
          hoverTableEl.style.width = `${maxWidth}px`;
          hoverTableEl.style.overflowX = "auto";
        }
      });
      return res;
    },
    onChartReady(chart) {
      chart.on("magictypechanged", (event) => {
        const selectedGraphType = event.currentType;
        this.selectedToolboxOption = selectedGraphType;
      });

      chart.on("restore", () => {
        this.updateGraphData();
      });

      if (this.isHoverFix) {
        chart.getZr().on("click", (params) => {
          const pointInPixel = [params.offsetX, params.offsetY];

          if (chart.containPixel("grid", pointInPixel)) {
            this.fixTableHandler(params.event);
          }
        });
      }
    },
    updateTypeType() {
      if (this.selectedToolboxOption) {
        this.bar.series.forEach((series) => {
          series.type = this.selectedToolboxOption;
        });
      }
    },
    updateGraphData() {
      this.dataLength = this.series.length;
      let tempSeries = [];
      const excludedType = ["pie", "heatmap"];
      if (!!this.dataLength && !excludedType.includes(this.series[0].type)) {
        this.series.forEach((el, index) => {
          if (el.xAxisIndex !== undefined) {
            this.$set(this.bar.dataZoom[index], "xAxisIndex", [0, 1]);
          }
          if (el.itemStyle) {
            el["itemStyle"].color = this.colors[index];
          } else {
            el["itemStyle"] = { color: this.colors[index] };
          }
          el["markPoint"] = {
            data: [
              {
                type: "max",
                name: "Max Value",
              },
              {
                type: "min",
                name: "Min Value"
              }
            ]
          }
          tempSeries.push(el);
        });
      } else {
        tempSeries = [...this.series];
      }
      this.bar.series = _.cloneDeep(tempSeries);
    },
  },
  watch: {
    theme() {
      this.reloadGraph += 1;
      this.$nextTick(() => {
        this.graphColorHandler();
        this.updateTypeType();
      });
    },
    series() {
      this.updateGraphData();
      this.reloadGraph += 1;
    },
    title() {
      this.bar.title = this.title;
    },
    xAxis: {
      handler(val) {
        this.bar.xAxis = val || { type: "category" };
      },
      deep: true,
    },
    yAxis: {
      handler(val) {
        this.bar.yAxis = val && val.length > 0 ? val : [{ type: "value" }];
      },
      deep: true,
    },
    xAxisData: {
      handler(val) {
        this.bar.xAxis.data = [...val];
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.echarts {
  height: 100%;
}
</style>